import { useQuery, useQueryClient } from "@tanstack/react-query";

import useRosterStatus from "./zustand/useRosterStatus";
import { useEffect, useRef, useState } from "react";
import { useLoginUser } from "./useAuth";
import { showRosterToast } from "@/pages/roster-create/components/rosterToast";
import useStore from "@/store/store";
import httpClient from "@/config/axios";
import { getInfeasibilityFromUnknown } from "@/types/roster-infeasibility";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

export interface JobStatusResponse {
  job_id: string;
  status: RosterJobStatus;
  progress: number | null;
  result_roster_id: string | null;
  error_message: string | null;
  updated_at: string | null; // ISO string
}

export type RosterJobStatus = "QUEUED" | "RUNNING" | "SUCCESS" | "FAILED";

const COMPLETED_JOB_TOAST_WINDOW_MS = 8000;
const PENDING_QUEUED_FALLBACK_MS = 1000;
const ROSTER_JOB_STATUS_QUERY_KEY = QUERY_KEYS.roster.jobStatus();

function isInProgressStatus(
  status: RosterJobStatus | null | undefined
): status is "QUEUED" | "RUNNING" {
  return status === "QUEUED" || status === "RUNNING";
}

function isCompletedStatus(
  status: RosterJobStatus | null | undefined
): status is "SUCCESS" | "FAILED" {
  return status === "SUCCESS" || status === "FAILED";
}

function isFreshCompletedJob(updatedAt: string | null) {
  if (!updatedAt) return false;

  const jobTime = new Date(updatedAt).getTime();
  if (Number.isNaN(jobTime)) return false;

  return Date.now() - jobTime <= COMPLETED_JOB_TOAST_WINDOW_MS;
}

function getAxiosStatus(error: unknown) {
  return (error as { response?: { status?: number } })?.response?.status;
}

async function fetchLatestRosterJobStatus() {
  try {
    return await httpClient.get<JobStatusResponse>(
      `/jobs/status/latest?_=${Date.now()}`
    );
  } catch (error: unknown) {
    const status = getAxiosStatus(error);

    console.log("[Polling] Error status:", status, "Error:", error);

    if (status === 404) {
      console.log("[Polling] Job not found (404), returning null");
      return null;
    }

    console.log("[Polling] Other error, throwing:", error);
    throw error;
  }
}

export default function useRosterJobPolling() {
  const loginUser = useLoginUser();
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<RosterJobStatus | null>(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);

  const { setRosterMade, resetRosterMadeState, setRosterInfeasibility } =
    useRosterStatus();
  const pollingTrigger = useStore((state) => state.pollingTrigger);
  const rosterMadeStatus = useStore((state) => state.rosterMade.status);
  const dismissedJobId = useStore((state) => state.dismissedJobId);

  const canFetchRosterJob =
    !!loginUser?.is_head_nurse || loginUser?.hn_auth === "HN";

  // pollingTrigger가 변경되면 폴링 시작
  useEffect(() => {
    if (pollingTrigger > 0) {
      console.log("[Polling] Trigger received, starting polling...");
      prevStatusRef.current = null;
      queryClient.removeQueries({ queryKey: ROSTER_JOB_STATUS_QUERY_KEY });
      setIsPollingEnabled(true);
    }
  }, [pollingTrigger, queryClient]);

  // store의 FAILED 상태 감지 시 폴링 즉시 중지
  useEffect(() => {
    if (rosterMadeStatus === "FAILED" && isPollingEnabled) {
      console.log("[Polling] FAILED detected in store, stopping polling...");
      setIsPollingEnabled(false);
    }
  }, [rosterMadeStatus, isPollingEnabled]);

  const query = useQuery<JobStatusResponse | null>({
    queryKey: ROSTER_JOB_STATUS_QUERY_KEY,
    queryFn: fetchLatestRosterJobStatus,
    refetchInterval: isPollingEnabled ? 3000 : false,
    refetchIntervalInBackground: true,
    refetchOnMount: "always",
    staleTime: 0,
    enabled: canFetchRosterJob,
    retry: false,
  });

  useEffect(() => {
    const shouldStartPendingFallback =
      canFetchRosterJob &&
      query.isFetching &&
      !dismissedJobId &&
      rosterMadeStatus == null;

    if (!shouldStartPendingFallback) return;

    const timerId = window.setTimeout(() => {
      const currentState = useStore.getState();
      const queryState = queryClient.getQueryState(ROSTER_JOB_STATUS_QUERY_KEY);
      const isStillPending = queryState?.fetchStatus === "fetching";

      if (
        isStillPending &&
        currentState.rosterMade.status == null &&
        !currentState.dismissedJobId
      ) {
        setRosterMade({ status: "RUNNING" });
        setRosterInfeasibility(null);
      }
    }, PENDING_QUEUED_FALLBACK_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    canFetchRosterJob,
    query.isFetching,
    queryClient,
    dismissedJobId,
    rosterMadeStatus,
    setRosterMade,
    setRosterInfeasibility,
  ]);

  useEffect(() => {
    const data = query.data;

    if (!canFetchRosterJob || data === undefined) {
      return;
    }

    const stopPolling = ({ resetRoster = false } = {}) => {
      if (isPollingEnabled) setIsPollingEnabled(false);

      if (resetRoster) {
        resetRosterMadeState();
        setRosterInfeasibility(null);
      }
    };

    if (!data || !data.job_id) {
      if (query.isFetching) return;

      console.log("[Polling] Job Id 없음");
      if (rosterMadeStatus) resetRosterMadeState();
      setRosterInfeasibility(null);
      stopPolling();
      return;
    }

    const status = data.status;
    const isLiveFailedDuringPolling = status === "FAILED" && isPollingEnabled;
    const failedInfeasibility =
      status === "FAILED"
        ? getInfeasibilityFromUnknown(data.error_message)
        : null;

    if (data.job_id === dismissedJobId && isCompletedStatus(status)) {
      console.log("[Polling] Dismissed completed job ignored");
      stopPolling({ resetRoster: true });
      return;
    }

    if (isCompletedStatus(status)) {
      if (isLiveFailedDuringPolling) {
        console.log(
          "[Polling] FAILED detected during polling, will update state (manual change possible)"
        );
      } else if (!isFreshCompletedJob(data.updated_at)) {
        console.log("[Polling] Completed job is stale or missing updated_at");
        stopPolling({ resetRoster: true });
        return;
      }
    }

    if (failedInfeasibility?.severity === "blocking") {
      setRosterInfeasibility(failedInfeasibility);
    }

    setRosterMade({ status });

    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status;
      showRosterToast({
        status,
        errorMessage:
          failedInfeasibility?.summary_message_ko ??
          data.error_message ??
          undefined,
      });
    }

    if (isInProgressStatus(status)) {
      if (!isPollingEnabled) {
        console.log("[Polling] Job in progress, enabling polling");
        setIsPollingEnabled(true);
      }
      return;
    }

    console.log("[Polling] Job completed, stopping polling");
    stopPolling();
  }, [
    query.data,
    query.isFetching,
    setRosterMade,
    resetRosterMadeState,
    setRosterInfeasibility,
    queryClient,
    isPollingEnabled,
    rosterMadeStatus,
    dismissedJobId,
    canFetchRosterJob,
  ]);

  return query;
}
